import React from "react";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@mui/material";
import { buildSignaturesTemplate, getDefaultRoleLabels, getNextSignerKeyAndEmail } from "../utils/signatureUtils";

const noSelectStyle = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const defaultRoleLabels = getDefaultRoleLabels();

export const SUBSEQUENT_SIGNING_ROLES = [
  { key: "client.buy", label: "Buyer" },
  { key: "client.sell", label: "Seller" },
  { key: "team.buy", label: "Buyer Agent" },
  { key: "team.sell", label: "Seller Agent" },
  { key: "other.managingBroker", label: "Managing Broker" },
  { key: "other.propertyManager", label: "Property Manager" },
];

/**
 * Which roles ACKNOWLEDGE this form after the fact.
 *
 * subsequentSigningJSON is shaped exactly like signaturesJSON — role metadata plus signer rows
 * carrying their own `fields[]` — so this card is the peer of Signer Roles and Initials Roles, and
 * a role ticked here is independent of whether that role signs the form itself.
 *
 * Three keys signaturesJSON does not have live on each block: `label` (what the acknowledger is
 * shown), `actionLabel` (their button) and `key` (the block's name). Those are authored in JSON
 * Preview, not here — ticking a role seeds them so a new block is publishable, and editing them is
 * a JSON job.
 *
 * @param {Object}   subsequentSigning        the subsequentSigningJSON map, or {}.
 * @param {Array}    bindings                 subsequentSigningAttributeBindings — fields placed
 *   inside the block. Unticking a role with fields still on the PDF would orphan them.
 * @param {Function} onSubsequentSigningChange called with the next map.
 * @param {Function} onBlocked                called with a message when unticking is refused.
 */
const SubsequentSigningRoleSelector = ({
  subsequentSigning,
  bindings = [],
  onSubsequentSigningChange,
  onBlocked,
}) => {
  const template = buildSignaturesTemplate();
  const current =
    subsequentSigning && typeof subsequentSigning === "object" && !Array.isArray(subsequentSigning)
      ? subsequentSigning
      : {};

  const boundCount = (roleKey) => bindings.filter((b) => b?.roleKey === roleKey).length;

  const handleChange = (roleKey, label) => {
    if (current[roleKey]) {
      const bound = boundCount(roleKey);
      if (bound > 0) {
        // Refuse rather than silently deleting fields the user cannot see from this card.
        onBlocked?.(
          `${label} still has ${bound} field${bound === 1 ? "" : "s"} placed on the PDF. ` +
            `Remove ${bound === 1 ? "it" : "them"} first, then untick the role.`,
        );
        return;
      }
      const { [roleKey]: _removed, ...rest } = current;
      onSubsequentSigningChange(rest);
      return;
    }

    const t = template[roleKey] || {};
    const { signerKey, email } = getNextSignerKeyAndEmail(roleKey, []);
    // The acknowledger is a single signer: one row, min/max/default all 1. altSigners, and the
    // label/actionLabel/key wording, are the author's to refine in JSON.
    onSubsequentSigningChange({
      ...current,
      [roleKey]: {
        integrationRole: t.integrationRole,
        meviDealRole: roleKey,
        min: 1,
        max: 1,
        default: 1,
        altSigners: [],
        label: "Acknowledgement of Receipt",
        actionLabel: "Submit",
        key: "subsequentSigning",
        signatures: [{ signerKey, email, fields: [] }],
      },
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Subsequent Signing Roles"
        subheader="Select the roles that ACKNOWLEDGE this form after it is signed. Shaped exactly like Signer Roles — each role gets one signer row whose fields you place on the PDF. The block's label, action label and key are edited in JSON Preview."
        sx={noSelectStyle}
      />
      <Divider />
      <CardContent sx={noSelectStyle}>
        <FormGroup row>
          {SUBSEQUENT_SIGNING_ROLES.map((role) => {
            const bound = boundCount(role.key);
            const effectiveLabel = defaultRoleLabels[role.key] ?? role.label;
            return (
              <FormControlLabel
                key={role.key}
                control={
                  <Checkbox
                    checked={!!current[role.key]}
                    onChange={() => handleChange(role.key, effectiveLabel)}
                    sx={{ "&.Mui-checked": { color: "#00a896" } }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                    <span>{effectiveLabel}</span>
                    {bound > 0 && (
                      <Typography variant="caption" sx={{ color: "#00a896", fontWeight: 600 }}>
                        {`${bound} field${bound === 1 ? "" : "s"}`}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ mr: 4 }}
              />
            );
          })}
        </FormGroup>
      </CardContent>
    </Card>
  );
};

export default SubsequentSigningRoleSelector;
